import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { WHATSAPP_URL, WHATSAPP_NUMBER } from "@/lib/brand";
import { User2, HeartHandshake, Users2, Sparkles, Rainbow, GraduationCap, MessageCircle } from "lucide-react";

const HONORARIOS_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hola Psychelibrium, me gustaría conocer los honorarios y el servicio que mejor se adapte a mis necesidades."
)}`;

const services = [
  {
    Icon: User2,
    title: "Terapia individual",
    body: "Un espacio para conocerte, comprender lo que sientes y construir herramientas para tu vida cotidiana. Ansiedad, depresión, autoestima, duelo, estrés.",
  },
  {
    Icon: HeartHandshake,
    title: "Terapia de pareja",
    body: "Recuperamos la conversación, reconocemos ciclos y trabajamos vínculos afectivos con enfoque sistémico y comunicacional.",
  },
  {
    Icon: Users2,
    title: "Terapia familiar",
    body: "Intervención con familias que atraviesan crisis, transiciones o dificultades en la crianza y en la convivencia.",
  },
  {
    Icon: GraduationCap,
    title: "Adolescentes",
    body: "Acompañamiento respetuoso para adolescentes: identidad, escuela, redes sociales, ansiedad y regulación emocional.",
  },
  {
    Icon: Sparkles,
    title: "Adultos",
    body: "Procesos de largo o corto plazo enfocados en metas terapéuticas claras y bienestar sostenido.",
  },
  {
    Icon: Rainbow,
    title: "Comunidad LGBTQ+",
    body: "Atención afirmativa y libre de prejuicios: identidad, orientación, familia, transiciones, vínculos y salud mental.",
  },
];

const Servicios = () => {
  return (
    <div data-testid="servicios-page">
      <PageHeader
        eyebrow="02 · Servicios"
        title="Terapia en línea con enfoque humano."
        subtitle="Diseñamos cada proceso según tu momento vital. Sesiones confidenciales, disponibles de 5 a 9 pm, desde cualquier lugar."
      />

      <section className="px-6 md:px-14 pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s, i) => (
          <article
            key={s.title}
            data-testid={`service-card-${i}`}
            className="card-soft p-6 rise"
            style={{ animationDelay: `${80 * (i + 1)}ms` }}
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: "rgba(74,21,33,0.06)", color: "var(--p-burgundy)" }}
            >
              <s.Icon size={20} strokeWidth={1.4} />
            </div>
            <h3
              className="mt-5"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontSize: "1.35rem",
                fontWeight: 600,
                color: "var(--p-black)",
              }}
            >
              {s.title}
            </h3>
            <p
              className="mt-2"
              style={{ fontFamily: "Cormorant Garamond, serif", color: "var(--p-brown)", lineHeight: 1.55 }}
            >
              {s.body}
            </p>
          </article>
        ))}
      </section>

      <section className="px-6 md:px-14 pb-24">
        <div
          className="grid grid-cols-1 md:grid-cols-3 rounded-sm overflow-hidden"
          style={{ background: "#fbf6ec", border: "1px solid rgba(138,116,101,0.2)" }}
        >
          <InfoCell title="Modalidad" value="100 % en línea" note="Videollamada segura" />
          <InfoCell title="Horario" value="5 : 00 · 9 : 00 pm" note="Zona horaria CDMX" divider />
          <InfoCell title="Duración" value="50 minutos" note="Sesiones semanales o quincenales" divider />
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="servicios-cta" className="btn-primary">Agenda tu primera sesión</a>
          <Link to="/enfoques" className="btn-ghost">Ver enfoques terapéuticos</Link>
        </div>

        {/* Honorarios */}
        <div
          data-testid="honorarios-section"
          className="mt-10 rounded-sm p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          style={{ background: "var(--p-burgundy)", color: "var(--p-cream)" }}
        >
          <div className="max-w-2xl">
            <p className="eyebrow" style={{ color: "var(--p-gold-soft)" }}>Honorarios</p>
            <h3
              className="mt-2"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontSize: "clamp(1.4rem, 2.4vw, 1.9rem)",
                fontWeight: 500,
                color: "var(--p-cream)",
              }}
            >
              ¿Deseas conocer los honorarios?
            </h3>
            <p
              className="mt-3"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                color: "rgba(245,239,230,0.85)",
                fontSize: "1.05rem",
                lineHeight: 1.6,
              }}
            >
              Contáctanos y con gusto te brindaremos la información sobre el
              servicio que mejor se adapte a tus necesidades.
            </p>
          </div>
          <a
            href={HONORARIOS_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="honorarios-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full whitespace-nowrap"
            style={{
              background: "var(--p-gold-soft)",
              color: "var(--p-burgundy)",
              fontFamily: "Cormorant Garamond, serif",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            <MessageCircle size={16} />
            Consultar por WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

const InfoCell = ({ title, value, note, divider }) => (
  <div className={`p-8 ${divider ? "md:border-l" : ""}`} style={{ borderColor: "rgba(138,116,101,0.25)" }}>
    <div className="eyebrow" style={{ color: "var(--p-burgundy)" }}>{title}</div>
    <div className="mt-2" style={{ fontFamily: "Cinzel, serif", fontSize: "1.5rem", color: "var(--p-black)", letterSpacing: "0.08em" }}>
      {value}
    </div>
    <div className="mt-2" style={{ fontFamily: "Cormorant Garamond, serif", color: "var(--p-brown)" }}>{note}</div>
  </div>
);

export default Servicios;
